import type {User} from 'nutshell-core/users/users-types';

import {Stack} from 'shells/layout/stack/stack';
import type {Tag} from 'shells/tags-editor/tags-editor';

import type {
    ContactLastContactedTimeFragment,
    ContactPageFragment,
    NutFileInput,
} from 'nutshell-graphql-types';

import {PeepHeaderActionBar} from '../../shared/header/action-bar/peep-header-action-bar';
import {PeepDescriptionContainer} from '../../shared/header/description/peep-description-container';
import {PeepMap} from '../../shared/header/map/peep-map';
import {PeepHeaderRightLayout} from '../../shared/header/peep-header-right-layout';
import {PeepTagsEditor} from '../../shared/tags-editor/peep-tags-editor';

import {useGetSmsEmailUnsubscribes} from '../sidebar/audiences-widget/graphql/hooks/use-get-sms-email-unsubscribes';

import {ContactAvatar} from './contact-avatar';
import {ContactContextOverview} from './contact-context-overview';
import {ContactDeliverablityStatusBadges} from './contact-deliverability-status-badges';
import {ContactDuplicateWrapper} from './contact-duplicate-wrapper';
import {ContactEditFields} from './contact-edit-fields';

import peepPageStyles from '../../peep-page.module.css';

interface Props {
    contact: ContactPageFragment;
    contactLastContact: ContactLastContactedTimeFragment | null;
    isLoadingContactLastContact: boolean;
    user: User;
    onUpdateContactName: (name: string) => Promise<any>;
    onUpdateAvatar: (avatarFile: NutFileInput | null) => Promise<any>;
    onUpdateAssignee: (assigneeId: string | null) => Promise<any>;
    onUpdateDeletedStatus: (isDeleted: boolean) => Promise<any>;
    onUpdateContactTerritory: (
        territoryId: string | null,
        territoryName: string | null
    ) => Promise<any>;
    onUpdateDescription: (description: string) => Promise<any>;
    onHardDelete: () => Promise<any>;
    onUpdateTags: (tags: Tag[]) => Promise<any>;
}

export function ContactHeader(props: Props) {
    const {isUnsubscribed, isLoading} = useGetSmsEmailUnsubscribes(props.contact.id);

    const hasAtLeastOneEnabledEditField = props.contact.headerFields.some((f) => f.isEnabled);

    const primaryAccountLocation =
        props.contact.primaryAccount?.node.postalAddresses.primary?.location;
    const contactLocation = props.contact.postalAddresses.primary?.location;
    const mapLocation = contactLocation ?? primaryAccountLocation ?? null;
    const mapCoordinates = mapLocation?.coordinates ?? null;
    const mapWeather = mapLocation?.weather ?? null;

    return (
        <div className={peepPageStyles['header-container']}>
            <PeepMap
                peepType='contact'
                locationCoordinates={mapCoordinates}
                locationWeather={mapWeather}
            />
            <div className={peepPageStyles['header-content']}>
                <div className={peepPageStyles['avatar-column']}>
                    <ContactAvatar contact={props.contact} onUpdateAvatar={props.onUpdateAvatar} />
                    {!NutClientConfig.hasCoreEntityFullHeightSidebarFF && (
                        <Stack alignItems='center' spacing={8}>
                            <ContactDuplicateWrapper contactId={props.contact.id} />
                            {NutClientConfig.hasSmsAudiencesFF && (
                                <ContactDeliverablityStatusBadges contactId={props.contact.id} />
                            )}
                        </Stack>
                    )}
                </div>
                <PeepHeaderRightLayout>
                    <PeepHeaderActionBar
                        peep={props.contact}
                        user={props.user}
                        entityType='contact'
                        assigneeId={props.contact.owner?.id ?? null}
                        assigneePlaceholder='Assign this person…'
                        onUpdateName={props.onUpdateContactName}
                        onUpdateAssignee={props.onUpdateAssignee}
                        onUpdateDeletedStatus={props.onUpdateDeletedStatus}
                        onHardDelete={props.onHardDelete}
                        isUnsubscribedFromAll={!isLoading ? isUnsubscribed : undefined}
                        nameAdornment={
                            NutClientConfig.hasCoreEntityFullHeightSidebarFF ? (
                                <ContactDuplicateWrapper contactId={props.contact.id} />
                            ) : undefined
                        }
                    />
                    <div className={peepPageStyles['context-overview-container']}>
                        <ContactContextOverview contact={props.contact} />
                    </div>
                    <div className='pl-2'>
                        <PeepDescriptionContainer
                            entityType='contact'
                            value={props.contact.description ?? ''}
                            onSaveNewValue={props.onUpdateDescription}
                        />
                    </div>
                    {!NutClientConfig.hasCoreEntityFullHeightSidebarFF && (
                        <>
                            {hasAtLeastOneEnabledEditField && (
                                <div className={peepPageStyles['edit-fields-container']}>
                                    <ContactEditFields
                                        contact={props.contact}
                                        contactLastContact={props.contactLastContact}
                                        isLoadingContactLastContact={
                                            props.isLoadingContactLastContact
                                        }
                                        canAccessSetup={props.user.permissions.canAccessSetup}
                                        onUpdateContactTerritory={props.onUpdateContactTerritory}
                                    />
                                </div>
                            )}

                            <div className={peepPageStyles['tags-editor-container']}>
                                <PeepTagsEditor
                                    entityId={props.contact.id}
                                    entityType='CONTACT'
                                    user={props.user}
                                    tagMaps={props.contact.tagMaps?.edges}
                                    onUpdateTags={props.onUpdateTags}
                                />
                            </div>
                        </>
                    )}
                </PeepHeaderRightLayout>
            </div>
        </div>
    );
}
